import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import I18n from 'I18n';
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSignupEmbedder } from 'signup-embedder/hooks/useSignupEmbedder';
import { ExperimentKeys } from '../experiments/ExperimentKeys';
import { shouldUseAnimationsGSWH } from '../experiments/gswhV1/gswhUtils';
import { TrackWhen, useExperiment } from '../experiments/useExperiment';
import { useButtonGridFocus } from '../hooks/useButtonGridFocus';
import { useButtonGridPreferredColumnCount } from '../hooks/useButtonGridPreferredColumnCount';
import { useCurrentStep } from '../hooks/useCurrentStep';
import { useIsFramedCentredStep } from '../hooks/useIsFramedCentredStep';
import { useResponsiveness } from '../responsiveness/ResponsivenessProvider';
import { getFlowKey } from '../routing/getFlowKey';
import { useSignupData } from '../signup-data/useSignupData';
import { ButtonsGridButton, ButtonsGridButtonHolder, ButtonsGridStyled, StyledButtonText, StyledIllustration, StyledIllustrationContainer, getButtonColumnCount } from '../styles/ButtonsGrid.styles';
import { EventName } from '../tracking/EventName';
import { getTracker } from '../tracking/getTracker';
import { useView } from '../views/useView';
import NextButtonWrapper from './NextButtonWrapper';
import { UseCaseIllustrations } from '../views/UseCaseView';
import { useLayoutTemplate } from '../hooks/useLayoutTemplate';
import { LayoutType } from 'signup-components/types';

const defaultGetButtonText = ({
  dataKey,
  key
}) => {
  return I18n.text(`views.${dataKey}.options.${key}`);
};

export const ButtonsGrid = ({
  columnCount,
  optionHeight,
  options,
  dataKey,
  stepKey,
  type,
  hasNextButton = false,
  nextButtonLabel,
  shouldDisableAutofocus = false,
  getButtonDescriptionText,
  getButtonText = defaultGetButtonText,
  onSelect = () => {},
  onHighlightChange = () => {},
  setDataFn,
  isSelected = () => false,
  shouldShowIllustrations
}) => {
  const gridRef = useRef(null);
  const [clickedNextButton, setClickedNextButon] = useState(false);
  const flowKey = getFlowKey();
  const {
    submit
  } = useView({
    stepKey
  });
  const {
    data,
    setData
  } = useSignupData(dataKey);
  const {
    preferredColumnCount
  } = useButtonGridPreferredColumnCount(options, dataKey, getButtonText);
  const {
    isAssignedToVariant: isACQ140Variant
  } = useExperiment(ExperimentKeys.ACQ0140, TrackWhen.never);
  const shouldUseTransition = useSelector(shouldUseAnimationsGSWH);
  const {
    currentStep
  } = useCurrentStep();
  const {
    isEmbedded
  } = useSignupEmbedder();
  const {
    isMobile
  } = useResponsiveness();
  const {
    layoutType
  } = useLayoutTemplate();
  const isFramedCentredStepLayout = layoutType === LayoutType.FramedCentredStep;
  const actualColumnCount = columnCount || preferredColumnCount || getButtonColumnCount(currentStep, isMobile, isEmbedded, isFramedCentredStepLayout);
  const {
    isFramedCentredStep
  } = useIsFramedCentredStep();

  const onSelectedChange = value => {
    setClickedNextButon(true);
    onSelect(value);

    if (shouldUseTransition) {
      const buttonContainer = document.getElementsByClassName('buttonsGrid')[0];
      buttonContainer.addEventListener('animationend', () => {
        submit();
      });
    } else {
      submit();
    }
  };

  useButtonGridFocus(gridRef, options.length, shouldDisableAutofocus);

  const onClickOption = key => {
    if (setDataFn) {
      setDataFn(key, setData);
    } else {
      setData(key);
    }

    if (!hasNextButton) {
      onSelectedChange(key);
    } else {
      onHighlightChange(key);
      getTracker().track(EventName.Interaction, {
        action: `highlight ${dataKey} ${key}`
      });
    }
  };

  const nthChild = actualColumnCount % 2 === 0 ? 'odd' : 'even';
  return /*#__PURE__*/_jsxs(ButtonsGridStyled // @ts-expect-error Expression produces a union type that is too complex to represent.
  , {
    className: "buttonsGrid",
    columnCount: columnCount,
    currentStep: currentStep,
    flowKey: flowKey,
    isFramedCentredStep: isFramedCentredStep,
    isFramedCentredStepLayout: isFramedCentredStepLayout,
    nthChild: nthChild,
    offset: actualColumnCount - 1,
    ref: gridRef,
    shouldCenter: options.length % actualColumnCount !== 0,
    children: [options.map((key, index) => /*#__PURE__*/_jsx(ButtonsGridButtonHolder, {
      minHeight: optionHeight,
      children: /*#__PURE__*/_jsx(ButtonsGridButton, {
        block: true,
        className: "ButtonsGrid_Button",
        currentStep: currentStep,
        "data-selenium": `${dataKey}-${index}`,
        "data-selenium-option": key,
        description: getButtonDescriptionText && getButtonDescriptionText({
          dataKey,
          key,
          index
        }),
        flowKey: flowKey,
        isACQ140Variant: isACQ140Variant(),
        isFramedCentredStep: isFramedCentredStep,
        name: 'selectable-button',
        selected: data === key || isSelected(key),
        tabIndex: index,
        title: /*#__PURE__*/_jsx(StyledButtonText, {
          className: "m-all-0",
          shouldShowIllustrations: shouldShowIllustrations,
          children: getButtonText({
            dataKey,
            key,
            index
          })
        }),
        truncatable: false,
        type: type,
        onClick: () => onClickOption(key),
        children: shouldShowIllustrations && /*#__PURE__*/_jsx(StyledIllustrationContainer, {
          children: /*#__PURE__*/_jsx(StyledIllustration, {
            name: UseCaseIllustrations.get(key)
          })
        })
      })
    }, key)), hasNextButton && /*#__PURE__*/_jsx(NextButtonWrapper, {
      label: nextButtonLabel,
      loading: clickedNextButton,
      onClick: () => onSelectedChange(data)
    })]
  });
};